import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useInView } from 'react-intersection-observer';
import { ArticleHero } from 'components/ArticleHero/ArticleHero';
import { RichText } from 'components/RichText/RichText';
import { Pills } from 'components/Tags/Pills';
import { AuthorFeature } from 'components/AuthorFeature/AuthorFeature';
import { Article as ArticleType } from 'data/SingleArticle/types';
import { UpNext } from 'components/Cards/UpNext/UpNext';
import { FloatingPopup } from 'components/Popups/FloatingPopup';
import { SubscribePopup } from 'components/Popups/Subscribe/SubscribePopup';
import { SignupPopup } from 'components/Popups/Signup/SignupPopup';
import { conditionalPopupCategories } from 'constants/popups';
import { mediaQueries } from 'components/mediaQueries';
import { SubscribeMini } from 'components/Subscribe/Mini/SubscribeMini';
import { SubscribeFormContainer } from 'components/Subscribe/Homepage/Container';
import { Spacer } from 'components/Spacer/Spacer';
import { TitleBanner } from 'components/TitleBanner/TitleBanner';
import { isPlacementTag } from 'data/global/helpers';

type Props = {
  article: ArticleType;
};

export default function Article({ article }: Props) {
  const [subscribeRef, subscribeRefInView] = useInView();
  const [popupIsVisible, setPopupIsVisible] = useState(false);
  const [heroRef, heroRefInView] = useInView();
  const [articleBodyRef, articleBodyRefInView] = useInView();
  const { body, title, tags, author, upNextArticles } = article;
  const tagsToDisplay = tags.reduce<string[]>((newTagsArray, tag) => {
    if (tag && !isPlacementTag(tag)) {
      newTagsArray.push(tag);
    }
    return newTagsArray;
  }, []);

  React.useEffect(() => {
    if (
      !heroRefInView &&
      !subscribeRefInView &&
      articleBodyRefInView &&
      !popupIsVisible
    ) {
      setPopupIsVisible(true);
    } else if (popupIsVisible) {
      setPopupIsVisible(false);
    }
  }, [heroRefInView, subscribeRefInView, articleBodyRefInView]);

  const renderConditionalPopup = () => {
    const categoryNames = article.categories.map(
      (category) => category.categoryName,
    );

    if (
      categoryNames.some((c) =>
        conditionalPopupCategories.SubscribePopup.includes(c),
      )
    ) {
      return (
        <FloatingPopup
          isVisible={popupIsVisible}
          setIsVisible={setPopupIsVisible}
        >
          <SubscribePopup />
        </FloatingPopup>
      );
    } else if (
      categoryNames.some((c) =>
        conditionalPopupCategories.SignupPopup.includes(c),
      )
    ) {
      return (
        <FloatingPopup
          isVisible={popupIsVisible}
          setIsVisible={setPopupIsVisible}
        >
          <SignupPopup />
        </FloatingPopup>
      );
    }
  };

  return (
    <Outer>
      {!heroRefInView && <TitleBanner title={title} />}
      <ArticleWrapper>
        <div ref={heroRef}>
          <ArticleHero article={article} />
        </div>
        <ArticleBodyWrapper>
          <div ref={articleBodyRef}>
            <RichText body={body} />
          </div>
          <Spacer hasLine={true} />
          <div ref={subscribeRef}>
            <SubscribeMini>
              <SubscribeFormContainer
                inputBackgroundColor="grey100"
                sectionToTrack="ArticlePageSubscribeArticleBottom"
              />
            </SubscribeMini>
          </div>
          <Spacer hasLine={true} />
          <AuthorFeature author={author[0]} />
          <Spacer hasLine={true} />
          {tagsToDisplay.length > 0 && (
            <Pills tags={tagsToDisplay} type="tags" />
          )}
          {tagsToDisplay.length > 0 && <Spacer hasLine={true} />}
          {upNextArticles.length > 0 && (
            <>
              <UpNext article={upNextArticles[0]} />
              <Spacer hasLine={true} />
            </>
          )}
        </ArticleBodyWrapper>
      </ArticleWrapper>
      {renderConditionalPopup()}
    </Outer>
  );
}

const Outer = styled.div`
  grid-template-columns: 1fr;
  display: grid;
  justify-content: center;
  place-items: center;
  position: relative;
  width: 100%;
`;

const ArticleWrapper = styled.div`
  padding: 0 20px;

  ${mediaQueries.tablet} {
    max-width: 600px;
    padding: 0;
  }
  ${mediaQueries.desktop} {
    max-width: 760px;
  }
`;

const ArticleBodyWrapper = styled.div`
  ${mediaQueries.desktop} {
    max-width: 700px;
    margin: 0 auto;
  }
`;
